import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import store from 'utils/store';
import { SignInWithAmazonV2 } from 'ecommerce/components';
import withFacebook from 'ecommerce/hoc/withFacebook';
import authApi from 'ecommerce/api/auth';
import { authContext } from 'ecommerce/lib/authError';
import useModalContext from 'components/context/modal';
import useDataContext from 'data/context/data';
import useUserContext from 'ecommerce/context/user';

import { getSafeRedirectPath } from 'utils/validator';
import { Head, Layout } from '../components/layout';
import AuthPanel from '../components/auth/authPanel';
import { AuthShell } from '../components/auth/parts';
import tracker from '../lib/tracker';
import { SESSION_REDIRECT_KEY } from '../lib/sessionKeys';

const SignInPage = () => {
  const dataContext = useDataContext();
  const userContext = useUserContext();
  const modalContext = useModalContext();
  const router = useRouter();

  useEffect(() => {
    if (router.asPath.indexOf('/signin') !== 0 && router.asPath.indexOf('/user') !== 0) {
      store.session.set(SESSION_REDIRECT_KEY, router.asPath);
    }
  }, [router.asPath]);

  // Capture redirect target passed from an SSR redirect (e.g. /drafts/:slug → /signin?redirect=...).
  useEffect(() => {
    const redirect = router.query.redirect;
    if (typeof redirect === 'string') {
      const safe = getSafeRedirectPath(redirect, null);
      if (safe) {
        store.session.set(SESSION_REDIRECT_KEY, safe);
      }
    }
  }, [router.query.redirect]);

  const setData = async (data, type = 'signin') => {
    userContext.setAccessToken(data.access_token);

    modalContext.toast(
      `You have been successfully ${type === 'signup' ? 'signed up' : 'signed in'}!`,
      {
        type: 'success',
      },
    );

    let url;
    if (router.asPath.indexOf('/signin') === 0) {
      if (store.session.has(SESSION_REDIRECT_KEY)) {
        url = getSafeRedirectPath(store.session.get(SESSION_REDIRECT_KEY, '/'));
      } else {
        url = '/';
      }
    } else {
      url = getSafeRedirectPath(router.asPath);
    }

    store.session.remove(SESSION_REDIRECT_KEY);
    await router.replace(url);
    window.scrollTo(0, 0);
  };

  //
  // SIGN IN WITH EMAIL
  //
  const trackEmailError = (errorType, isSignUp) => {
    tracker.onCustomEvent(isSignUp ? 'signup_email_error' : 'signin_email_error', {
      error_type: errorType,
      context: authContext(router.pathname),
    });
  };

  const handleOnSignedIn = async (data, type) => {
    await setData(data, type);
    if (type === 'signup') {
      tracker.signUp(data);
    } else {
      tracker.signIn(data);
    }
  };

  const trackAuthEvent = (action, payload = {}) => {
    tracker.onCustomEvent(action, { ...payload, context: authContext(router.pathname) });
  };

  //
  // SIGN IN WITH AMAZON (v2)
  //
  // SignInWithAmazonV2 already redeems the buyerToken via /api/v1/amazon/buyer
  // and hands us the signed-in user (with access_token) directly.
  const handleOnAmazonSignInSuccess = async data => {
    setData(data);
    tracker.signInWithAmazon(data);
  };

  const handleOnAmazonSignInFailed = error => {
    tracker.onCustomEvent('signin_amazon_error');
    console.error(error);
    modalContext.toast(
      'Problem with sign in with Amazon. Please try again or use different method.',
      {
        type: 'warning',
      },
    );
  };

  //
  // SIGN IN WITH FACEBOOK
  //
  const handleOnFacebookSignInSuccess = async facebookUserData => {
    const { data, error } = await authApi(dataContext.countryCode).signInWithFacebook(
      facebookUserData.id,
      facebookUserData.name,
      facebookUserData.email,
    );

    if (data) {
      setData(data);
      tracker.signInWithFacebook(data);
    } else {
      tracker.onCustomEvent('signin_facebook_error');
      console.error(error);
      modalContext.toast(
        'Problem with sign in with Facebook. Please try again or use different method.',
        {
          type: 'warning',
        },
      );
    }
  };

  return (
    <>
      <Head title="Login" noindex />

      <Layout className="au-page">
        <AuthShell>
          <AuthPanel
            initialMode={typeof router.query.mode === 'string' ? router.query.mode : undefined}
            countryCode={dataContext.countryCode}
            onSignedIn={handleOnSignedIn}
            onTrackError={trackEmailError}
            onTrack={trackAuthEvent}
            amazon={
              <SignInWithAmazonV2
                redirectUrl="/signin"
                countryCode={dataContext.countryCode}
                onSuccess={handleOnAmazonSignInSuccess}
                onFailed={handleOnAmazonSignInFailed}
                className="au-amazon"
              />
            }
          />
        </AuthShell>
      </Layout>
    </>
  );
};

export default withFacebook(SignInPage);
